import { useState, useRef, useCallback, useContext } from 'react';
import { useSelector } from 'react-redux';
import IntegrationPageSectionContext from '../Page/IntegrationPageSection/StateManagement/IntegrationPageSectionContext';

/**
 * A React hook for continuously polling an endpoint.
 * @param {string} urlTemplate - A template string for the URL (e.g., '/api/data/:id').
 * @param {function} fetcher - A function that fetches data from the endpoint.
 * @param {number} interval - Polling interval in milliseconds.
 * @returns {object} - Contains the latest data, an error object, and a method to start and stop polling.
 */

function usePolling(urlTemplate, clientId, interval = 5000) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const pollingRef = useRef(null);
  const isPolling = useRef(false);
  const fetcherRef = useRef(null);
  const csrfToken = useSelector((state) => state?.app?.csrfToken);
  const appIntegrationType = useSelector((state) => state?.app?.appIntegrationType);
  const { OrderConfigurator: { sellerId } = {} } = useContext(IntegrationPageSectionContext);

  // Store fetcher in ref, to prevent recreating the fetcher each time
  fetcherRef.current = async (url, clientId, csrfToken) => {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': csrfToken,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ clientId, appIntegrationType, sellerId })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  };

  const startPolling = useCallback(
    (params) => {
      if (isPolling.current) return;

      isPolling.current = true;
      // Function to replace placeholders in url template with the actual values
      const interpolateUrl = (template, values) => {
        let interpolatedUrl = template;
        if (values) {
          for (const key in values) {
            if (Object.prototype.hasOwnProperty.call(values, key)) {
              interpolatedUrl = interpolatedUrl.replace(`:${key}`, values[key]);
            }
          }
        }
        return interpolatedUrl;
      };

      const poll = async () => {
        try {
          const url = interpolateUrl(urlTemplate, params);
          const result = await fetcherRef.current(url, clientId, csrfToken);
          setData(result);
          setError(null);
        } catch (err) {
          setError(err);
        } finally {
          if (isPolling.current) {
            pollingRef.current = setTimeout(poll, interval);
          }
        }
      };

      poll();
    },
    [urlTemplate, interval]
  );

  const stopPolling = useCallback(() => {
    isPolling.current = false;
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
    }
  }, []);

  return { data, error, startPolling, stopPolling };
}

export default usePolling;
